import {
  MEMBER_PERMISSION,
  MEMBER_STAGING_PERMISSION,
  type MemberEnvironment,
} from '@cortex/domain-ac-mens-hub-api';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MemberEnvironmentTabs } from '../components/member/MemberEnvironmentTabs.js';
import {
  MemberListFilters,
  type MemberListFilterValues,
} from '../components/member/MemberListFilters.js';
import { useMemberList } from '../hooks/useMember.js';
import { usePermissions } from '../hooks/usePermissions.js';
import { RENTAL_STATUS_STYLES, STATUS_STYLES } from '../lib/member-constants.js';

/** @graph-connects none */
const LIMIT = 50;

/** @graph-connects none */
type SortBy = 'accountCreatedAt' | 'subscriptionCreatedAt';

/** @graph-connects none */
type SortOrder = 'asc' | 'desc';

/** @graph-connects none */
const thBase =
  'px-3.5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]';

/** @graph-connects none */
const tdBase = 'whitespace-nowrap px-3.5 py-2.5';

/**
 * @graph-node {Page}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 会員一覧ページ。検索条件+ページネーション付きの会員テーブルを表示し、行クリックで詳細ページへ遷移する。レンタルステータス(複数選択)・プラン・チケット保有でも絞り込める。
 * @graph-connects useMemberList [calls] 会員一覧取得
 * @graph-connects MemberListFilters [calls] 検索条件の入力
 */
export function MemberListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission, isLoading: permLoading } = usePermissions();
  const canViewProduction = hasPermission(MEMBER_PERMISSION);
  const canViewStaging = hasPermission(MEMBER_STAGING_PERMISSION);
  // 利用者がタブを選ぶまでは state に確定させず、権限から毎回導出する。
  // usePermissions はロード中 permissions が既定値 ['home'] になり canViewProduction が
  // false を返すため、初期値として useState に焼き込むと本番権限だけを持つ人が staging に
  // 固定される（useState の初期値は再評価されず、タブも showTabs=false で出ないため戻せない）。
  const [selectedEnvironment, setSelectedEnvironment] = useState<MemberEnvironment | null>(null);
  // 本番権限が無い人に本番タブを初期表示しても 403 になるだけなので、見られる方を開く
  const environment: MemberEnvironment =
    selectedEnvironment ?? (canViewProduction ? 'production' : 'staging');
  const setEnvironment = setSelectedEnvironment;
  const isStaging = environment === 'staging';
  const [copiedEmail, setCopiedEmail] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<SortBy | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [appliedFilters, setAppliedFilters] = useState<MemberListFilterValues>({});

  const { data, isLoading, error } = useMemberList(
    {
      environment,
      page,
      limit: LIMIT,
      ...appliedFilters,
      sortBy,
      sortOrder: sortBy ? sortOrder : undefined,
    },
    // 権限が確定するまでは environment が導出できないので取得しない
    { enabled: !permLoading },
  );

  /** @graph-connects none */
  function handleApply(values: MemberListFilterValues) {
    setAppliedFilters(values);
    setPage(1);
  }

  /** @graph-connects none */
  function handleClear() {
    setAppliedFilters({});
    setSortBy(undefined);
    setSortOrder('desc');
    setPage(1);
  }

  /**
   * テスト実施者が最終的に必要なのはログイン用のメールアドレスなので、
   * 目で追って手で写さなくて済むようにコピーまで到達させる。
   *
   * @graph-connects none
   */
  function copyEmail(email: string) {
    const done = () => {
      setCopiedEmail(email);
      window.setTimeout(() => {
        setCopiedEmail(null);
      }, 1600);
    };
    if (navigator.clipboard as Clipboard | undefined) {
      void navigator.clipboard.writeText(email).then(done, done);
      return;
    }
    done();
  }

  /** @graph-connects none */
  function handleSort(column: SortBy) {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
    setPage(1);
  }

  const totalCount = data?.totalCount ?? 0;
  const offset = (page - 1) * LIMIT;
  const totalPages = Math.max(1, Math.ceil(totalCount / LIMIT));

  return (
    <div className="mx-auto max-w-[1280px] p-6">
      <div className="mb-5 flex items-baseline gap-3">
        <h1 className="text-xl font-semibold text-[var(--color-text-primary)]">
          {t('memberList.title')}
        </h1>
        {data?.snapshotAt && (
          <span className="rounded bg-[var(--color-bg-elevated)] px-2 py-0.5 text-[11px] text-[var(--color-text-muted)]">
            {t('common.dataUpdate')}: {formatJst(data.snapshotAt)}
          </span>
        )}
      </div>

      <MemberEnvironmentTabs
        environment={environment}
        canViewProduction={canViewProduction}
        canViewStaging={canViewStaging}
        onChange={(next) => {
          // 検索条件は持ち越す。「本番で見つけた条件をそのまま STG で試す」が主な使い方
          setEnvironment(next);
          setPage(1);
        }}
      />

      <MemberListFilters environment={environment} onApply={handleApply} onClear={handleClear} />

      <div className="overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
        {permLoading || isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="loading-spinner" />
          </div>
        ) : error ? (
          <div className="py-12 text-center text-sm text-[var(--color-error)]">
            {t('common.fetchError')}
          </div>
        ) : !data?.rows.length ? (
          <div className="py-12 text-center text-sm text-[var(--color-text-muted)]">
            {t('memberList.noResults')}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
                    <th className={thBase}>ID</th>
                    <th className={thBase}>{t('memberList.fullName')}</th>
                    <th className={thBase}>{t('memberList.email')}</th>
                    <th className={thBase}>{t('memberList.tel')}</th>
                    <th className={thBase}>{t('memberList.status')}</th>
                    <th className={thBase}>{t('memberList.plan')}</th>
                    <th className={thBase}>{t('memberList.rentalStatus')}</th>
                    <th className={thBase}>{t('memberList.ticket')}</th>
                    <SortableHeader
                      label={t('memberList.accountCreatedAt')}
                      column="accountCreatedAt"
                      currentSortBy={sortBy}
                      currentSortOrder={sortOrder}
                      onSort={handleSort}
                    />
                    <SortableHeader
                      label={t('memberList.subscriptionCreatedAt')}
                      column="subscriptionCreatedAt"
                      currentSortBy={sortBy}
                      currentSortOrder={sortOrder}
                      onSort={handleSort}
                    />
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row) => {
                    const st = STATUS_STYLES[row.subscriptionStatus ?? ''];
                    const rentalStyle = RENTAL_STATUS_STYLES[row.rentalStatus ?? ''];
                    return (
                      <tr
                        key={row.arthurUserId}
                        onClick={
                          isStaging
                            ? undefined
                            : () => {
                                void navigate({
                                  to: '/members/$arthurUserId',
                                  params: { arthurUserId: String(row.arthurUserId) },
                                });
                              }
                        }
                        title={isStaging ? t('memberList.stagingNoDetail') : undefined}
                        className={`border-b border-[var(--color-border)] transition-colors hover:bg-[var(--color-bg-elevated)] ${
                          isStaging ? '' : 'cursor-pointer'
                        }`}
                      >
                        <td className={`${tdBase} font-mono text-[12px]`}>
                          {String(row.arthurUserId)}
                        </td>
                        <td className={`${tdBase} text-[13px]`}>
                          {isStaging ? <MaskedCell /> : formatFullName(row.lastName, row.firstName)}
                        </td>
                        <td className={`${tdBase} text-[13px]`}>
                          <span className="inline-flex items-center gap-2">
                            <span>{row.email ?? '-'}</span>
                            {isStaging && row.email !== null && (
                              <button
                                type="button"
                                onClick={() => {
                                  copyEmail(row.email ?? '');
                                }}
                                className="rounded border border-[var(--color-border)] px-1.5 py-0.5 text-[10px] text-[var(--color-text-muted)] transition-colors hover:border-[var(--color-warning)] hover:text-[var(--color-warning)]"
                              >
                                {copiedEmail === row.email
                                  ? t('memberList.copiedEmail')
                                  : t('memberList.copyEmail')}
                              </button>
                            )}
                          </span>
                        </td>
                        <td className={`${tdBase} font-mono text-[12px]`}>
                          {isStaging ? <MaskedCell /> : (row.tel ?? '-')}
                        </td>
                        <td className={tdBase}>
                          {st ? (
                            <span
                              className="inline-block rounded-full px-2 py-0.5 text-[11px] font-medium"
                              style={{ background: st.bg, color: st.text }}
                            >
                              {st.label}
                            </span>
                          ) : (
                            <span className="text-[12px] text-[var(--color-text-muted)]">-</span>
                          )}
                        </td>
                        <td
                          className="max-w-[190px] truncate px-3.5 py-2.5 text-[12px] text-[var(--color-text-secondary)]"
                          title={row.planName ?? undefined}
                        >
                          {row.planName ?? '-'}
                        </td>
                        <td className={tdBase}>
                          {rentalStyle && row.rentalStatus !== null ? (
                            <span
                              className="inline-block rounded-full px-2 py-0.5 text-[11px] font-medium"
                              style={{ background: rentalStyle.bg, color: rentalStyle.text }}
                            >
                              {row.rentalStatus}
                            </span>
                          ) : (
                            <span className="text-[12px] text-[var(--color-text-muted)]">-</span>
                          )}
                        </td>
                        <td className={`${tdBase} text-[12px] tabular-nums`}>
                          <span
                            className={
                              row.ticketCount > 0
                                ? 'text-[var(--color-text-primary)]'
                                : 'text-[var(--color-text-disabled)]'
                            }
                          >
                            {t('memberList.ticketUnit', { n: row.ticketCount })}
                          </span>
                        </td>
                        <td
                          className={`${tdBase} text-[12px] tabular-nums text-[var(--color-text-secondary)]`}
                        >
                          {row.accountCreatedAt ? formatDateJst(row.accountCreatedAt) : '-'}
                        </td>
                        <td
                          className={`${tdBase} text-[12px] tabular-nums text-[var(--color-text-secondary)]`}
                        >
                          {row.subscriptionCreatedAt
                            ? formatDateJst(row.subscriptionCreatedAt)
                            : '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between border-t border-[var(--color-border)] px-3.5 py-2.5 text-[12px] text-[var(--color-text-secondary)]">
              <span className="tabular-nums">
                {isStaging && (
                  <span className="mr-3 text-[var(--color-text-disabled)]">
                    {t('memberList.stagingNoDetail')}
                  </span>
                )}
                {t('memberList.pagination', {
                  total: String(totalCount),
                  from: String(offset + 1),
                  to: String(Math.min(offset + LIMIT, totalCount)),
                })}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => {
                    setPage((p) => Math.max(1, p - 1));
                  }}
                  className="rounded-md border border-[var(--color-border)] px-3 py-1 text-xs transition-colors hover:bg-[var(--color-bg-elevated)] disabled:opacity-40"
                >
                  {t('common.prev')}
                </button>
                <span className="tabular-nums text-xs text-[var(--color-text-muted)]">
                  {String(page)} / {String(totalPages)}
                </span>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => {
                    setPage((p) => p + 1);
                  }}
                  className="rounded-md border border-[var(--color-border)] px-3 py-1 text-xs transition-colors hover:bg-[var(--color-bg-elevated)] disabled:opacity-40"
                >
                  {t('common.next')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * STG で伏せる項目の表示。
 *
 * 空欄やハイフンにすると「データが無い」と読めてしまうため、伏せていることと
 * その理由が分かる形で出す。
 *
 * @graph-connects none
 */
function MaskedCell() {
  const { t } = useTranslation();
  return (
    <span
      title={t('memberList.maskedReason')}
      className="cursor-help border-b border-dashed border-[var(--color-border-hover)] text-[12px] tracking-wider text-[var(--color-text-disabled)]"
    >
      {t('memberList.masked')}
    </span>
  );
}

/**
 * 姓と名を1列にまとめる。片方だけ登録されている会員がいるため、
 * 欠けている側を空文字として連結せず、あるものだけを並べる。
 *
 * @graph-connects none
 */
function formatFullName(lastName: string | null, firstName: string | null): string {
  const parts = [lastName, firstName].filter(
    (part): part is string => part !== null && part !== '',
  );
  return parts.length > 0 ? parts.join(' ') : '-';
}

/** @graph-connects none */
function SortableHeader({
  label,
  column,
  currentSortBy,
  currentSortOrder,
  onSort,
}: {
  label: string;
  column: SortBy;
  currentSortBy: SortBy | undefined;
  currentSortOrder: SortOrder;
  onSort: (column: SortBy) => void;
}) {
  const isActive = currentSortBy === column;
  return (
    <th
      className="cursor-pointer select-none px-3.5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-text-primary)]"
      onClick={() => {
        onSort(column);
      }}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <span className="inline-flex flex-col text-[8px] leading-none">
          <span
            style={{
              color:
                isActive && currentSortOrder === 'asc'
                  ? 'var(--color-accent-primary)'
                  : 'var(--color-text-muted)',
            }}
          >
            ▲
          </span>
          <span
            style={{
              color:
                isActive && currentSortOrder === 'desc'
                  ? 'var(--color-accent-primary)'
                  : 'var(--color-text-muted)',
            }}
          >
            ▼
          </span>
        </span>
      </span>
    </th>
  );
}

/** @graph-connects none */
function formatJst(isoDate: string): string {
  const _d = new Date(isoDate);
  if (Number.isNaN(_d.getTime())) return isoDate;
  return _d.toLocaleString('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** @graph-connects none */
function formatDateJst(isoDate: string): string {
  const _d = new Date(isoDate);
  if (Number.isNaN(_d.getTime())) return isoDate;
  return _d.toLocaleDateString('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}
